// store/effects/users.effects.ts
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as UsersActions from '../actions/users.action';
import { ApiService } from '../../services/api.service';
import { catchError, map, mergeMap, of, takeUntil, switchMap, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { getUser, getUserFailure, getUserSuccess } from '../actions/users.action';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class UsersEffects {
  constructor(
    private actions$: Actions,
    private api: ApiService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.loadUsers),
      switchMap(({ query }) =>
        this.api.searchUsers(query).pipe(
          takeUntil(this.actions$.pipe(ofType(UsersActions.clearUsers))),
          map((users) => {
            return UsersActions.loadUsersSuccess({ users });
          }),
          catchError((error) => {
            this.toastr.error(this.translate.instant('USER_SEARCH_FAIL'));
            return of(UsersActions.loadUsersFailure({ error }));
          })
        )
      )
    )
  );

  getUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getUser),
      mergeMap(({ userId }) =>
        this.api.getUserById(userId).pipe(
          map((fetchedUser) => getUserSuccess({ user: fetchedUser })),
          catchError((error) => of(getUserFailure({ error: error.message })))
        )
      )
    )
  );

  getUserSuccess$ = createEffect(() => this.actions$.pipe(ofType(getUserSuccess)), {
    dispatch: false,
  });

  getUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getUserFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('USER_GET_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.updateUser),
      mergeMap(({ user, selectedFile }) =>
        this.api.editUser(user, selectedFile).pipe(
          map((updatedUser) => {
            this.toastr.success(this.translate.instant('USER_UPDATE_SUCCESS'));
            return UsersActions.updateUserSuccess({ user: updatedUser });
          }),
          catchError((error) => {
            this.toastr.error(this.translate.instant('USER_UPDATE_FAIL'));
            return of(UsersActions.updateUserFailure({ error }));
          })
        )
      )
    )
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.deleteUser),
      mergeMap(({ id }) =>
        this.api.deleteUser(id).pipe(
          map(() => {
            this.toastr.success(this.translate.instant('USER_DELETE_SUCCESS'));
            return UsersActions.deleteUserSuccess({ id });
          }),
          catchError((error) => {
            this.toastr.error(this.translate.instant('USER_DELETE_FAIL'));
            return of(UsersActions.deleteUserFailure({ error }));
          })
        )
      )
    )
  );
}
